import React from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { OFFICE, whatsappUrl, telUrl, mailUrl, mapUrl, mapEmbedUrl } from "../lib/office";

export default function Contact() {
  const { t } = useLang();

  const blocks = [
    { icon: MapPin, label: t("office.addressLabel"), value: t("office.address"), action: mapUrl(), cta: t("common.itinerary"), external: true, key: "address" },
    { icon: Phone, label: t("office.phoneLabel"), value: OFFICE.phoneFixed, action: telUrl(true), cta: t("common.call"), key: "phone-fixed" },
    { icon: MessageCircle, label: t("office.phoneMobileLabel"), value: OFFICE.phoneMobile, action: whatsappUrl(t("whatsappMessages.general")), cta: t("common.whatsapp"), external: true, key: "phone-mobile" },
    { icon: Mail, label: t("office.emailLabel"), value: OFFICE.email, action: mailUrl(), cta: t("common.email"), key: "email" },
    { icon: Clock, label: t("office.hoursLabel"), value: t("office.hours"), key: "hours" },
  ];

  return (
    <div>
      <section className="border-b border-border-light">
        <div className="container-notary py-20 md:py-24">
          <div className="overline mb-4">{t("contact.eyebrow")}</div>
          <h1 className="text-4xl md:text-5xl font-serif text-charcoal max-w-3xl">{t("contact.title")}</h1>
          <span className="divider-gold my-6 block" />
          <p className="text-lg text-charcoal-muted max-w-2xl">{t("contact.subtitle")}</p>
        </div>
      </section>

      <section className="container-notary py-16 md:py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5 grid grid-cols-1 gap-px bg-border-light border border-border-light self-start" data-testid="contact-blocks">
          {blocks.map(({ icon: Icon, label, value, action, cta, external, key }) => (
            <div key={key} className="bg-surface p-6 flex items-start gap-4">
              <Icon className="w-5 h-5 text-gold mt-1 shrink-0" />
              <div className="flex-1">
                <div className="overline mb-1">{label}</div>
                <div className="text-charcoal font-serif">{value}</div>
                {action && cta && (
                  <a
                    href={action}
                    {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="inline-flex items-center gap-2 mt-2 text-xs uppercase tracking-[0.2em] text-gold hover:text-gold-hover"
                    data-testid={`contact-action-${key}`}
                  >
                    {cta} <ArrowRight className="w-3 h-3 rtl-flip" />
                  </a>
                )}
              </div>
            </div>
          ))}
          <div className="bg-surface p-6">
            <Link to="/rendez-vous" className="btn-gold w-full" data-testid="contact-cta-appointment">
              {t("common.bookAppointment")}
            </Link>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="overline mb-3">{t("contact.mapTitle")}</div>
          <div className="border border-border-light h-[420px] md:h-[560px] overflow-hidden">
            <iframe
              title="Google map"
              src={mapEmbedUrl()}
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
